import { jsx, jsxs } from "react/jsx-runtime";
import styled, { css } from "styled-components";
import { InputCheckbox } from "@creditornot/cb-checkbox";
import { forwardRef, useId } from "react";
import { shade } from "polished";
import { colors, typographyCss, cbSemanticCssVars } from "@creditornot/cb-ingredients";
import { createCssVars, createThemedCssVarDefinitions, configureCssVars, themedConfigToThemedAttrs } from "@creditornot/cb-utils";
import { InputRadio } from "@creditornot/cb-radio";
const SvgCaretright = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M19.5 11.972c0-.662-.283-1.292-.778-1.73L7.592.433a1.84 1.84 0 00-2.568.195 1.85 1.85 0 00.134 2.578l9.732 8.579a.25.25 0 010 .376l-9.732 8.578a1.849 1.849 0 00.609 3.17 1.84 1.84 0 001.825-.397l11.126-9.807a2.312 2.312 0 00.782-1.732z" }) }));
const CaretRightIcon = SvgCaretright;
const colorCssVars$1 = createCssVars(["bgHover", "bgActive"]);
const menuItemBaseStyle = css`
  ${createThemedCssVarDefinitions({
  bgHover: { light: colors.pepper8, dark: colors.salt12 },
  bgActive: { light: colors.pepper12, dark: colors.salt16 }
})}

  box-sizing: border-box;
  border: none;
  user-select: none;

  width: 100%;
  min-width: 0;
  text-align: start;
  ${typographyCss.Body3()}
  font-weight: 400;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  min-height: 28px;
  padding: 4px 8px;
  color: ${cbSemanticCssVars.textPrimary};
  background-color: transparent;
  border-radius: 8px;

  &:not(:active) {
    transition: background-color 48ms ease-in;
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
  :focus-visible {
    outline: 2px transparent solid;
    box-shadow: 0 0 0 2px ${cbSemanticCssVars.a11yFocusIndicator};
  }

  &:not([data-disabled]):not(:disabled) {
    &:hover:not([data-managed-focus]) {
      background-color: ${colorCssVars$1.bgHover};
    }

    &:active {
      background-color: ${colorCssVars$1.bgActive};
    }

    cursor: pointer;
  }

  &[data-disabled],
  &:disabled {
    cursor: not-allowed;
  }

  &:not([disabled]):not([data-selected]) {
    &:hover:not([data-managed-focus]) {
      background-color: ${colorCssVars$1.bgHover};
    }

    &&:active {
      background-color: ${colorCssVars$1.bgActive};
    }
  }

  &[data-highlighted]:not([data-selected]) {
    background-color: ${colorCssVars$1.bgHover};
  }
`;
const variantConfigs = {
  black: {
    light: {
      bgHover: colors.pepper8,
      bgActive: colors.pepper12,
      color: colors.pepper100,
      colorDisabled: colors.pepper40,
      largeIconBg: colors.pepper8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    lightHighContrast: {
      bgHover: colors.pepper8,
      bgActive: colors.pepper12,
      color: colors.pepper100,
      colorDisabled: colors.pepper40,
      largeIconBg: colors.pepper8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    dark: {
      bgHover: colors.salt12,
      bgActive: colors.salt16,
      color: colors.salt100,
      colorDisabled: colors.salt48,
      largeIconBg: colors.salt12,
      subLabelColor: colors.salt72,
      subLabelColorDisabled: colors.salt36
    }
  },
  grey: {
    light: {
      bgHover: colors.pepper8,
      bgActive: colors.pepper12,
      color: colors.pepper64,
      colorDisabled: colors.pepper40,
      largeIconBg: colors.pepper8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    lightHighContrast: {
      bgHover: colors.pepper8,
      bgActive: colors.pepper12,
      color: colors.pepper64,
      colorDisabled: colors.pepper40,
      largeIconBg: colors.pepper8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    dark: {
      bgHover: colors.salt12,
      bgActive: colors.salt16,
      color: colors.salt68,
      colorDisabled: colors.salt44,
      largeIconBg: colors.salt12,
      subLabelColor: colors.salt72,
      subLabelColorDisabled: colors.salt36
    }
  },
  blue: {
    light: {
      bgHover: colors.wolt8,
      bgActive: colors.wolt12,
      color: colors.wolt100,
      colorDisabled: colors.wolt40,
      largeIconBg: colors.wolt8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    lightHighContrast: {
      bgHover: colors.darkSky8,
      bgActive: colors.darkSky12,
      color: shade(0.1)(colors.darkSky100),
      colorDisabled: colors.darkSky40,
      largeIconBg: colors.darkSky8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    dark: {
      bgHover: colors.wolt12,
      bgActive: colors.wolt16,
      color: colors.wolt100,
      colorDisabled: colors.wolt44,
      largeIconBg: colors.wolt12,
      subLabelColor: colors.salt72,
      subLabelColorDisabled: colors.salt36
    }
  },
  red: {
    light: {
      bgHover: colors.strawberry8,
      bgActive: colors.strawberry12,
      color: colors.strawberry100,
      colorDisabled: colors.strawberry40,
      largeIconBg: colors.strawberry8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    lightHighContrast: {
      bgHover: colors.strawberry8,
      bgActive: colors.strawberry12,
      color: shade(0.17)(colors.strawberry100),
      colorDisabled: colors.strawberry40,
      largeIconBg: colors.strawberry8,
      subLabelColor: colors.pepper64,
      subLabelColorDisabled: colors.pepper28
    },
    dark: {
      bgHover: colors.strawberry12,
      bgActive: colors.strawberry16,
      color: colors.strawberry100,
      colorDisabled: colors.strawberry44,
      largeIconBg: colors.strawberry12,
      subLabelColor: colors.salt72,
      subLabelColorDisabled: colors.salt36
    }
  }
};
const variantCssVars = createCssVars([
  "bgHover",
  "bgActive",
  "color",
  "colorDisabled",
  "largeIconBg",
  "subLabelColor",
  "subLabelColorDisabled"
]);
const { cssString: commonColorCssVarDefinitions, cssVars: commonColorCssVars } = configureCssVars({
  backgroundSelected: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  backgroundSelectedDisabled: {
    light: colors.wolt80,
    lightHighContrast: colors.darkSky80,
    dark: colors.wolt80
  },
  backgroundHover: {
    light: colors.wolt92,
    lightHighContrast: colors.darkSky92,
    dark: colors.wolt92
  },
  backgroundPressed: {
    light: colors.wolt84,
    lightHighContrast: colors.darkSky84,
    dark: colors.wolt84
  },
  textSelected: {
    light: colors.salt100,
    dark: colors.space100
  },
  textSelectedDisabled: {
    light: colors.salt80,
    lightHighContrast: colors.salt88,
    dark: colors.space72
  },
  largeIconBgSelected: {
    light: colors.salt8,
    dark: colors.pepper12
  }
});
const SubLabel = styled.div`
  ${typographyCss.Small()}
`;
const IconContainer$1 = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 12px;
  }
`;
const LargeIconContainer = styled(IconContainer$1)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ $selected }) => $selected ? commonColorCssVars.largeIconBgSelected : variantCssVars.largeIconBg};
  svg {
    width: 50%;
  }
`;
const rootCss = css`
  ${commonColorCssVarDefinitions}
  ${menuItemBaseStyle}

  ${({ $variant }) => createThemedCssVarDefinitions(themedConfigToThemedAttrs(variantConfigs[$variant]))}

  &[data-selected] {
    font-weight: 700;
  }

  &[disabled]:not([data-selected]) {
    &&,
    &&:hover,
    &&:active {
      background-color: transparent;
      color: ${variantCssVars.colorDisabled};
      cursor: not-allowed;
    }

    ${SubLabel} {
      color: ${variantCssVars.subLabelColorDisabled};
    }

    svg {
      fill: ${variantCssVars.colorDisabled};
    }
  }

  &[data-selected][disabled] {
    &&,
    &&:hover,
    &&:active {
      background-color: ${commonColorCssVars.backgroundSelectedDisabled};
      color: ${commonColorCssVars.textSelectedDisabled};
      cursor: not-allowed;

      ${IconContainer$1} svg {
        fill: ${commonColorCssVars.textSelectedDisabled};
      }
    }
  }

  &[data-selected]:not([disabled]) {
    background-color: ${commonColorCssVars.backgroundSelected};
    color: ${commonColorCssVars.textSelected};

    &:hover:not([data-managed-focus]) {
      background-color: ${commonColorCssVars.backgroundHover};
    }
    &&:active {
      background-color: ${commonColorCssVars.backgroundPressed};
    }

    ${IconContainer$1} svg {
      fill: ${commonColorCssVars.textSelected};
    }
  }

  &:not([disabled]):not([data-selected]) {
    color: ${variantCssVars.color};

    ${SubLabel} {
      color: ${variantCssVars.subLabelColor};
    }

    &:hover:not([data-managed-focus]) {
      background-color: ${variantCssVars.bgHover};
    }

    &&:active {
      background-color: ${variantCssVars.bgActive};
    }

    ${IconContainer$1} svg {
      fill: ${variantCssVars.color};
    }
  }

  &[data-highlighted]:not([data-selected]) {
    background-color: ${variantCssVars.bgHover};
  }
`;
const Root$5 = styled.button`
  ${rootCss}
`;
const LeftContent = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-inline-end: 8px;
  padding-inline-end: ${({ $rightPadding }) => $rightPadding}px;
`;
const RightContent$1 = styled.div`
  margin-inline-start: 8px;
  flex: 0;
`;
const ContentContainer = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Content$4 = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const MenuActionItem = forwardRef(
  ({
    children,
    focused,
    icon,
    leftContent,
    rightContent,
    role = "menuitem",
    selected = false,
    subLabel,
    tabIndex = 0,
    variant = "black",
    ...rest
  }, ref) => /* @__PURE__ */ jsxs(
    Root$5,
    {
      $variant: variant,
      "data-highlighted": focused ? true : void 0,
      "data-managed-focus": focused !== void 0 ? true : void 0,
      "data-selected": selected ? true : void 0,
      role,
      tabIndex,
      ...rest,
      "data-cb-menu-item": true,
      ref,
      type: "button",
      children: [
        !!leftContent && /* @__PURE__ */ jsx(LeftContent, { $rightPadding: subLabel ? 4 : 0, children: leftContent }),
        !leftContent && !!icon && /* @__PURE__ */ jsx(LeftContent, { $rightPadding: subLabel ? 4 : 0, children: subLabel ? /* @__PURE__ */ jsx(LargeIconContainer, { $selected: selected, children: icon }) : /* @__PURE__ */ jsx(IconContainer$1, { children: icon }) }),
        /* @__PURE__ */ jsxs(ContentContainer, { children: [
          /* @__PURE__ */ jsx(Content$4, { children }),
          !!subLabel && /* @__PURE__ */ jsx(SubLabel, { children: subLabel })
        ] }),
        !!rightContent && /* @__PURE__ */ jsx(RightContent$1, { children: rightContent })
      ]
    }
  )
);
MenuActionItem.displayName = "MenuActionItem";
const Root$4 = styled.div``;
const Item = styled.button`
  ${rootCss};
`;
const LeftIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-inline-end: 8px;

  svg {
    width: 12px;
  }
`;
const RightIconContainer = styled(LeftIconContainer)`
  padding-inline-end: 0;
  padding-inline-start: 8px;
`;
const StyledCaretIcon = styled(CaretRightIcon)`
  fill: currentColor;
  transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1);
  transform: ${({ $direction, theme: { cbDirection = "ltr" } }) => {
  if ($direction === "down") {
    return "rotate(90deg)";
  }
  return cbDirection === "ltr" ? "none" : "scaleX(-1)";
}};

  [dir='rtl'] && {
    transform: ${({ $direction }) => $direction === "down" ? "rotate(90deg)" : "scaleX(-1)"};
  }
`;
const ItemContent = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Collapse = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 500ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 500ms cubic-bezier(0.16, 1, 0.3, 1);

  &[aria-hidden='false'] {
    grid-template-rows: 1fr;
    opacity: 1;
  }
`;
const CollapsedContent$1 = styled.div`
  padding-inline-start: 28px;
  overflow: hidden;

  [data-cb-menu-item] {
    margin-inline-start: -8px;
    margin-inline-end: -8px;
  }

  > * {
    margin-top: 8px;
  }
`;
const MenuAccordionItem = ({
  accordionContent,
  active = false,
  caretPosition = "left",
  children,
  disabled = false,
  focused,
  menuItemRef,
  open = false,
  tabIndex = 0,
  variant = "black",
  ...rest
}) => {
  const collapsedContentId = useId();
  return /* @__PURE__ */ jsxs(Root$4, { "data-cb-menu-item": true, children: [
    /* @__PURE__ */ jsxs(
      Item,
      {
        $variant: variant,
        "aria-controls": collapsedContentId,
        "aria-expanded": open,
        "data-highlighted": focused ? true : void 0,
        "data-managed-focus": focused !== void 0 ? true : void 0,
        "data-selected": active ? true : void 0,
        disabled,
        tabIndex,
        ...rest,
        ref: menuItemRef,
        type: "button",
        children: [
          caretPosition === "left" && /* @__PURE__ */ jsx(LeftIconContainer, { children: /* @__PURE__ */ jsx(StyledCaretIcon, { $direction: open ? "down" : "right" }) }),
          /* @__PURE__ */ jsx(ItemContent, { children }),
          caretPosition === "right" && /* @__PURE__ */ jsx(RightIconContainer, { children: /* @__PURE__ */ jsx(StyledCaretIcon, { $direction: open ? "down" : "right" }) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(Collapse, { "aria-hidden": !open, "data-foo": 123, children: /* @__PURE__ */ jsx(CollapsedContent$1, { id: collapsedContentId, children: accordionContent }) })
  ] });
};
const Container = styled.div`
  display: flex;
  align-items: center;
`;
const Content$3 = styled.div`
  flex: 1;
  padding-inline-start: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const CollapsedContent = styled.div`
  padding-inline-start: 24px;

  [data-cb-menu-item] {
    margin-inline-start: -8px;
    margin-inline-end: -8px;
  }

  > * + * {
    margin-top: 8px;
  }
`;
const MenuAccordionCheckboxItem = ({
  accordionContent,
  checkboxProps = {},
  children,
  disabled,
  ...rest
}) => /* @__PURE__ */ jsx(
  MenuAccordionItem,
  {
    accordionContent: /* @__PURE__ */ jsx(CollapsedContent, { children: accordionContent }),
    disabled,
    ...rest,
    children: /* @__PURE__ */ jsxs(Container, { children: [
      /* @__PURE__ */ jsx(
        InputCheckbox,
        {
          ...checkboxProps,
          disabled: checkboxProps.disabled === void 0 ? disabled : checkboxProps.disabled,
          onClick: (event) => {
            event.stopPropagation();
            if (checkboxProps.onClick) {
              checkboxProps.onClick(event);
            }
          }
        }
      ),
      /* @__PURE__ */ jsx(Content$3, { children })
    ] })
  }
);
const Root$3 = styled.label`
  ${menuItemBaseStyle}
`;
const CheckboxContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline-end: 8px;
`;
const Content$2 = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MenuCheckboxItem = ({
  checked,
  children,
  disabled,
  focused,
  onBlur,
  onChange,
  onClick,
  onFocus,
  partiallyChecked,
  role = "menuitemcheckbox",
  selected,
  tabIndex = -1,
  value,
  ...rest
}, ref) => /* @__PURE__ */ jsxs(
  Root$3,
  {
    "data-disabled": disabled ? true : void 0,
    "data-highlighted": focused ? true : void 0,
    "data-managed-focus": focused !== void 0 ? true : void 0,
    "data-selected": selected ? true : void 0,
    ...rest,
    "data-cb-menu-item": true,
    ref,
    children: [
      /* @__PURE__ */ jsx(CheckboxContainer, { children: /* @__PURE__ */ jsx(
        InputCheckbox,
        {
          checked,
          disableFocusAndActiveStyles: true,
          disabled,
          onBlur,
          onChange,
          onClick,
          onFocus,
          partiallyChecked,
          readOnly: !onChange,
          role,
          tabIndex,
          value
        }
      ) }),
      /* @__PURE__ */ jsx(Content$2, { children })
    ]
  }
);
const MenuCheckboxItem$1 = forwardRef(MenuCheckboxItem);
const Root$2 = styled.div`
  box-sizing: border-box;
`;
const Header = styled.div`
  ${typographyCss.Small()}
  color: ${cbSemanticCssVars.textSecondary};
  margin-bottom: 12px;
`;
const Items = styled.div`
  > [data-cb-menu-item] {
    width: calc(100% + 16px);
    margin-inline-start: -8px;
  }

  > * + * {
    margin-top: 8px;
  }
`;
const MenuItemGroup = forwardRef(
  ({ children, role = "group", title, ...rest }, ref) => /* @__PURE__ */ jsxs(Root$2, { role, ...rest, "data-cb-menu-item": true, ref, children: [
    !!title && /* @__PURE__ */ jsx(Header, { children: title }),
    /* @__PURE__ */ jsx(Items, { children })
  ] })
);
MenuItemGroup.displayName = "MenuItemGroup";
const colorCssVars = createCssVars(["rightContentText", "rightContentTextSelected"]);
const Root$1 = styled.a`
  ${rootCss}
  text-decoration: none;

  ${createThemedCssVarDefinitions({
  rightContentText: {
    light: colors.pepper64,
    dark: colors.salt72
  },
  rightContentTextSelected: {
    light: colors.salt100,
    dark: colors.space100
  }
})}
`;
const IconContainer = styled(IconContainer$1)`
  flex-shrink: 0;
  padding-inline-end: 8px;

  svg {
    width: 16px;
  }
`;
const Content$1 = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ $fontWeight }) => $fontWeight === "bold" ? 700 : 400};
`;
const RightContent = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-inline-start: 8px;
  text-align: end;
  color: ${({ $selected }) => $selected ? colorCssVars.rightContentTextSelected : colorCssVars.rightContentText};
`;
const MenuLinkItem = forwardRef(
  ({
    active = false,
    children,
    disabled = false,
    focused,
    icon,
    rightContent,
    role = "menuitem",
    tabIndex = 0,
    variant = "black",
    ...rest
  }, ref) => /* @__PURE__ */ jsxs(
    Root$1,
    {
      $variant: variant,
      "data-highlighted": focused ? true : void 0,
      "data-managed-focus": focused !== void 0 ? true : void 0,
      "data-selected": active ? true : void 0,
      disabled,
      ref,
      role,
      tabIndex,
      ...rest,
      "data-cb-menu-item": true,
      children: [
        !!icon && /* @__PURE__ */ jsx(IconContainer, { children: icon }),
        /* @__PURE__ */ jsx(Content$1, { $fontWeight: active ? "bold" : "normal", children }),
        !!rightContent && /* @__PURE__ */ jsx(RightContent, { $selected: active, children: rightContent })
      ]
    }
  )
);
MenuLinkItem.displayName = "MenuLinkItem";
const Root = styled.label`
  ${menuItemBaseStyle}
`;
const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline-end: 8px;
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;
const MenuRadioItem = ({
  checked,
  children,
  disabled,
  focused,
  onBlur,
  onChange,
  onClick,
  onFocus,
  role = "menuitemradio",
  selected,
  tabIndex = -1,
  value,
  ...rest
}, ref) => /* @__PURE__ */ jsxs(
  Root,
  {
    "data-disabled": disabled ? true : void 0,
    "data-highlighted": focused ? true : void 0,
    "data-managed-focus": focused !== void 0 ? true : void 0,
    "data-selected": selected ? true : void 0,
    ...rest,
    "data-cb-menu-item": true,
    ref,
    children: [
      /* @__PURE__ */ jsx(RadioContainer, { children: /* @__PURE__ */ jsx(
        InputRadio,
        {
          checked,
          disableFocusAndActiveStyles: true,
          disabled,
          onBlur,
          onChange,
          onClick,
          onFocus,
          readOnly: !onChange,
          role,
          tabIndex,
          value
        }
      ) }),
      /* @__PURE__ */ jsx(Content, { children })
    ]
  }
);
const MenuRadioItem$1 = forwardRef(MenuRadioItem);
export {
  MenuAccordionCheckboxItem,
  MenuAccordionItem,
  MenuActionItem,
  MenuCheckboxItem$1 as MenuCheckboxItem,
  MenuItemGroup,
  MenuLinkItem,
  MenuRadioItem$1 as MenuRadioItem
};
//# sourceMappingURL=index.js.map
